import React from "react"
import { Link } from "gatsby"
import { Helmet } from "react-helmet"
import Layout from "../../components/Layout"
import useImmersion from "../../components/hotels/useImmersion"
import AuthorBox from "../../components/hotels/AuthorBox"
import ImmersiveBreak from "../../components/hotels/ImmersiveBreak"
import GuideCards from "../../components/hotels/GuideCards"

const SITE_URL = "https://gadait-international.com"

const PAGE_STYLE = `
  .mg-page { font-family:"SF Pro Display","SF Pro Text",-apple-system,BlinkMacSystemFont,"Helvetica Neue",Arial,sans-serif; background:#fff; color:#1d1d1f; }
  .mg-section { padding:72px 0; }
  .mg-section-soft { background:#f5f5f7; padding:72px 0; }
  .mg-section-dark { background:#050505; color:#f5f5f7; padding:80px 0; }
  .mg-container { max-width:860px; margin:0 auto; padding:0 24px; }
  .mg-h1 { font-size:clamp(30px,4.6vw,56px); font-weight:600; letter-spacing:-0.045em; line-height:1.08; color:#1d1d1f; margin-bottom:20px; }
  .mg-h2 { font-size:clamp(24px,3vw,38px); font-weight:600; letter-spacing:-0.04em; line-height:1.1; color:#1d1d1f; margin-bottom:20px; }
  .mg-h2-light { color:#f5f5f7; }
  .mg-body { font-size:17px; line-height:1.7; color:#3d3d3f; margin-bottom:18px; }
  .mg-body-light { color:rgba(245,245,247,0.72); }
  .mg-label { font-size:11px; letter-spacing:0.18em; text-transform:uppercase; color:#6e6e73; margin-bottom:10px; display:block; }
  .mg-label-light { color:rgba(245,245,247,0.48); }
  .mg-zone-card { border:1px solid rgba(0,0,0,0.08); border-radius:16px; padding:20px; color:#1d1d1f; display:block; background:#fff; }
  .ios-cta { display:inline-flex; align-items:center; justify-content:center; min-height:44px; border-radius:999px; padding:0 1.4rem; font-size:16px; font-weight:500; letter-spacing:-0.01em; white-space:nowrap; border:1px solid transparent; text-decoration:none; transition:transform 220ms cubic-bezier(0.22,1,0.36,1),background 260ms; cursor:pointer; }
  .ios-cta-primary { background:#0071e3; color:#fff; box-shadow:0 8px 20px rgba(0,113,227,0.24); }
  .ios-cta-primary:hover { background:#0077ed; transform:translateY(-1px); }
  .mm-rule { display:flex; gap:16px; margin-bottom:24px; align-items:flex-start; }
  .mm-rule-icon { flex-shrink:0; width:36px; height:36px; border-radius:50%; background:rgba(0,113,227,0.08); color:#0071e3; font-size:16px; font-weight:700; display:flex; align-items:center; justify-content:center; }
`

const CONTENT = {
  fr: {
    path: "/fr/notre-methode-editoriale/",
    altPath: "/en/our-editorial-method/",
    seoTitle: "Notre méthode éditoriale — hôtels, faits et transparence | GADAIT",
    seoDescription:
      "Comment GADAIT écrit ses pages hôtels et voyages : distinction affichée entre maisons partenaires et sélection indépendante, faits vérifiés au Guide Michelin et sur les supports publics des maisons, tarifs directionnels sourcés et datés, re-vérification trimestrielle, zéro avis inventé.",
    h1: "Notre méthode éditoriale",
    intro: [
      "Les pages voyage de GADAIT — la collection Hôtels & Palaces, les comparatifs, les guides — sont écrites pour une clientèle qui engage des budgets importants sur notre parole. Cette page dit précisément comment cette parole est construite, ce que nous vérifions, et ce que nous nous interdisons.",
    ],
    twoLevelsLabel: "La règle fondatrice",
    twoLevelsTitle: "Partenaire ou sélection : toujours affiché",
    twoLevels: [
      "Notre collection a deux niveaux, et chaque page affiche le sien. <strong>« Partenaire officiel »</strong> signifie qu'un accord réel nous lie à la maison — aujourd'hui Monteverdi Tuscany, en Toscane, et Maison Bauchart, à Paris. <strong>« Sélection GADAIT »</strong> signifie une recommandation purement éditoriale : aucun lien commercial, aucune commission de la maison, aucun contenu relu ou approuvé par elle.",
      "Pour un brief donné, nous recommandons la maison qui sert le client — partenaire ou pas. Nos duels (« Monteverdi ou Rosewood ? ») le montrent : quand le brief penche vers une maison non partenaire, c'est écrit noir sur blanc.",
    ],
    rulesLabel: "Les règles",
    rulesTitle: "Ce que nous vérifions, ce que nous nous interdisons",
    rules: [
      { icon: "✓", t: "Chaque fait est sourcé", d: "Clés et étoiles relevées au Guide Michelin ; capacités, offres et saisons relevées sur les supports publics des maisons ; notes voyageurs citées avec leur plateforme et leur volume d'avis. Quand un chiffre est un ordre de grandeur, il est présenté comme tel — jamais comme un prix garanti." },
      { icon: "✓", t: "Chaque page est datée", d: "Les relevés portent une date visible (« relevé le 28/08/2026 »), et les sections périssables — offres du moment, saisons — sont re-vérifiées chaque trimestre. Une offre expirée est retirée, pas laissée pourrir." },
      { icon: "✓", t: "Les photos sont créditées", d: "Les visuels des maisons proviennent de leurs supports publics ou de leurs press kits, et sont crédités « Photos © maison » sous chaque galerie. Nous demandons les kits officiels et corrigeons sur simple demande d'une maison." },
      { icon: "✗", t: "Zéro avis inventé", d: "Nous ne publions pas d'avis clients maison ni de notes attribuées par nous-mêmes. La preuve sociale citée est toujours externe, publique et vérifiable (Guide Michelin, Condé Nast Traveler, Tatler, plateformes d'avis)." },
      { icon: "✗", t: "Zéro tarif inventé", d: "Les tarifs affichés sont directionnels, sourcés (moteurs publics, supports des maisons) et datés. Chaque maison fixe ses prix ; ils se confirment à la cotation. Réserver via GADAIT ne coûte rien de plus." },
      { icon: "✗", t: "Zéro contenu dicté", d: "Aucune maison — partenaire incluse — ne relit ni n'approuve nos textes. Les corrections factuelles demandées par une maison sont appliquées ; les jugements éditoriaux restent les nôtres." },
    ],
    whoLabel: "Qui écrit",
    whoTitle: "Une équipe, un responsable",
    who: [
      "Les pages sont écrites et assumées par <strong>Pierre-Axel Gadait</strong>, fondateur de GADAIT International, avec l'équipe conseil séjours. Ce ne sont pas des contenus d'agence ni des textes générés en masse : chaque maison de la collection a été choisie lentement, et les recommandations engagent notre nom.",
      "Une erreur factuelle ? Écrivez-nous — la correction est appliquée et la page re-datée.",
    ],
    ctaTitle: "La collection, à cette exigence",
    ctaBody: "Cinq maisons toscanes, 11 Clés Michelin cumulées, des comparatifs honnêtes et des guides datés.",
    ctaBtn: "Découvrir Hôtels & Palaces",
    ctaHref: "/fr/hotels-et-palaces/",
    relatedLabel: "Continuer",
    relatedTitle: "Les pages écrites avec cette méthode",
    related: [
      { href: "/fr/hotels-et-palaces/", label: "Hôtels & Palaces — la collection" },
      { href: "/fr/plus-beaux-hotels-luxe-toscane/", label: "Les 5 plus beaux hôtels de Toscane" },
      { href: "/fr/monteverdi-ou-rosewood-castiglion-del-bosco/", label: "Monteverdi ou Rosewood ? Le match" },
      { href: "/fr/maison-bauchart-ou-four-seasons-george-v/", label: "Maison Bauchart ou Four Seasons George V ? Le match" },
      { href: "/fr/se-marier-en-toscane/", label: "Se marier en Toscane — le guide" },
    ],
    breadcrumbHome: "Accueil", breadcrumbHub: "Hôtels & Palaces", hubHref: "/fr/hotels-et-palaces/",
  },
  en: {
    path: "/en/our-editorial-method/",
    altPath: "/fr/notre-methode-editoriale/",
    seoTitle: "Our Editorial Method — Hotels, Facts and Transparency | GADAIT",
    seoDescription:
      "How GADAIT writes its hotel and travel pages: a displayed distinction between partner houses and independent selection, facts checked against the Michelin Guide and the houses' public materials, sourced and dated directional rates, quarterly re-verification, zero invented reviews.",
    h1: "Our Editorial Method",
    intro: [
      "GADAIT's travel pages — the Hotels & Palaces collection, the comparisons, the guides — are written for a clientele that commits serious budgets on our word. This page states precisely how that word is built, what we verify, and what we forbid ourselves.",
    ],
    twoLevelsLabel: "The founding rule",
    twoLevelsTitle: "Partner or Selection: Always Displayed",
    twoLevels: [
      "Our collection has two levels, and every page displays its own. <strong>'Official partner'</strong> means a real agreement ties us to the house — today Monteverdi Tuscany, in Tuscany, and Maison Bauchart, in Paris. <strong>'GADAIT selection'</strong> means a purely editorial recommendation: no commercial tie, no commission from the house, no content reviewed or approved by it.",
      "For a given brief, we recommend the house that serves the client — partner or not. Our head-to-heads ('Monteverdi or Rosewood?') show it: when a brief leans toward a non-partner house, it is written in black and white.",
    ],
    rulesLabel: "The rules",
    rulesTitle: "What We Verify, What We Forbid Ourselves",
    rules: [
      { icon: "✓", t: "Every fact is sourced", d: "Keys and stars checked against the Michelin Guide; capacities, offers and seasons taken from the houses' public materials; traveller ratings quoted with their platform and review volume. When a figure is an order of magnitude, it is presented as one — never as a guaranteed price." },
      { icon: "✓", t: "Every page is dated", d: "Checks carry a visible date ('seen on 28/08/2026'), and perishable sections — current offers, seasons — are re-verified quarterly. An expired offer is removed, not left to rot." },
      { icon: "✓", t: "Photos are credited", d: "The houses' visuals come from their public materials or press kits, credited 'Photos © house' under every gallery. We request official kits and correct anything at a house's simple request." },
      { icon: "✗", t: "Zero invented reviews", d: "We publish no in-house client reviews and no self-assigned ratings. Social proof is always external, public and verifiable (Michelin Guide, Condé Nast Traveler, Tatler, review platforms)." },
      { icon: "✗", t: "Zero invented rates", d: "Displayed rates are directional, sourced (public engines, the houses' materials) and dated. Each house sets its prices; they are confirmed at quotation. Booking through GADAIT costs nothing extra." },
      { icon: "✗", t: "Zero dictated content", d: "No house — partner included — reviews or approves our texts. Factual corrections requested by a house are applied; editorial judgments remain ours." },
    ],
    whoLabel: "Who writes",
    whoTitle: "One Team, One Name on It",
    who: [
      "Pages are written and owned by <strong>Pierre-Axel Gadait</strong>, founder of GADAIT International, with the stay advisory team. This is not agency content or mass-generated text: every house in the collection was chosen slowly, and the recommendations carry our name.",
      "Spotted a factual error? Write to us — the correction is applied and the page re-dated.",
    ],
    ctaTitle: "The Collection, Held to This Standard",
    ctaBody: "Five Tuscan houses, 11 Michelin Keys between them, honest comparisons and dated guides.",
    ctaBtn: "Discover Hotels & Palaces",
    ctaHref: "/en/hotels-and-palaces/",
    relatedLabel: "Keep reading",
    relatedTitle: "Pages Written With This Method",
    related: [
      { href: "/en/hotels-and-palaces/", label: "Hotels & Palaces — the collection" },
      { href: "/en/best-luxury-hotels-tuscany/", label: "The 5 Best Luxury Hotels in Tuscany" },
      { href: "/en/monteverdi-or-rosewood-castiglion-del-bosco/", label: "Monteverdi or Rosewood? The Match" },
      { href: "/en/maison-bauchart-or-four-seasons-george-v/", label: "Maison Bauchart or Four Seasons George V? The Match" },
      { href: "/en/tuscany-wedding-guide/", label: "Getting Married in Tuscany — the Guide" },
    ],
    breadcrumbHome: "Home", breadcrumbHub: "Hotels & Palaces", hubHref: "/en/hotels-and-palaces/",
  },
}

const EditorialMethodPage = ({ locale = "fr" }) => {
  useImmersion()
  const c = CONTENT[locale]
  const canonicalUrl = `${SITE_URL}${c.path}`
  const altUrl = `${SITE_URL}${c.altPath}`
  const breadcrumbSchema = { "@context":"https://schema.org", "@type":"BreadcrumbList", itemListElement: [
    { "@type":"ListItem", position:1, name:c.breadcrumbHome, item:`${SITE_URL}/${locale}/` },
    { "@type":"ListItem", position:2, name:c.breadcrumbHub, item:`${SITE_URL}${c.hubHref}` },
    { "@type":"ListItem", position:3, name:c.h1, item:canonicalUrl } ] }
  const articleSchema = { "@context":"https://schema.org", "@type":"Article", headline:c.seoTitle,
    description:c.seoDescription, datePublished:"2026-08-29", dateModified:"2026-08-29",
    author:{ "@type":"Person", name:"Pierre-Axel Gadait", jobTitle:"Fondateur, GADAIT International", url:SITE_URL },
    publisher:{ "@type":"Organization", name:"GADAIT International", url:SITE_URL, logo:{ "@type":"ImageObject", url:`${SITE_URL}/icons/icon-512x512.png` } },
    mainEntityOfPage:{ "@type":"WebPage", "@id":canonicalUrl } }

  return (
    <Layout locale={locale}>
      <Helmet>
        <html lang={locale} />
        <title>{c.seoTitle}</title>
        <meta name="description" content={c.seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={locale === "fr" ? "en" : "fr"} href={altUrl} />
        <link rel="alternate" hrefLang="x-default" href={locale === "en" ? canonicalUrl : altUrl} />
        <meta property="og:title" content={c.h1} />
        <meta property="og:description" content={c.seoDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <style>{PAGE_STYLE}</style>

      <div className="mg-page">

        <section className="mg-section" style={{ paddingTop:120 }}>
          <div className="mg-container">
            <nav style={{ fontSize:13, color:"#6e6e73", marginBottom:16 }}>
              <Link to={c.hubHref} style={{ color:"#6e6e73", textDecoration:"none" }}>{c.breadcrumbHub}</Link>{" › "}
              <span>{c.h1}</span>
            </nav>
            <h1 className="mg-h1">{c.h1}</h1>
            {c.intro.map((p, i) => <p key={i} className="mg-body" style={{ fontSize:19 }}>{p}</p>)}
          </div>
        </section>

        <section className="mg-section-soft">
          <div className="mg-container">
            <span className="mg-label">{c.twoLevelsLabel}</span>
            <h2 className="mg-h2">{c.twoLevelsTitle}</h2>
            {c.twoLevels.map((p, i) => <p key={i} className="mg-body" dangerouslySetInnerHTML={{ __html: p }} />)}
          </div>
        </section>

        <section className="mg-section">
          <div className="mg-container">
            <span className="mg-label">{c.rulesLabel}</span>
            <h2 className="mg-h2">{c.rulesTitle}</h2>
            <div style={{ marginTop:28 }}>
              {c.rules.map(r => (
                <div key={r.t} className="mm-rule">
                  <div className="mm-rule-icon" style={r.icon === "✗" ? { background:"rgba(255,59,48,0.08)", color:"#c0392b" } : {}}>{r.icon}</div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:17, marginBottom:4 }}>{r.t}</div>
                    <div style={{ fontSize:15, color:"#3d3d3f", lineHeight:1.65 }}>{r.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mg-section-soft">
          <div className="mg-container">
            <span className="mg-label">{c.whoLabel}</span>
            <h2 className="mg-h2">{c.whoTitle}</h2>
            {c.who.map((p, i) => <p key={i} className="mg-body" dangerouslySetInnerHTML={{ __html: p }} />)}
            <div style={{ marginTop:24 }}><AuthorBox locale={locale} /></div>
          </div>
        </section>

        <ImmersiveBreak
          img="/hotels/monteverdi/archway.webp"
          alt={locale === "fr" ? "Porche de pierre restauré — Monteverdi Tuscany" : "A restored stone archway — Monteverdi Tuscany"}
          eyebrow={locale === "fr" ? "La bellezza se vérifie" : "La bellezza is verified"}
          title={locale === "fr" ? "La confiance est<br />un travail manuel" : "Trust is<br />handmade"}
          sub={locale === "fr" ? "Pierre par pierre, fait par fait — c'est la même patience." : "Stone by stone, fact by fact — the same patience."}
        />

        <section className="mg-section-dark">
          <div className="mg-container" style={{ textAlign:"center" }}>
            <h2 className="mg-h2 mg-h2-light">{c.ctaTitle}</h2>
            <p className="mg-body mg-body-light" style={{ maxWidth:560, margin:"0 auto 28px" }}>{c.ctaBody}</p>
            <Link to={c.ctaHref} className="ios-cta ios-cta-primary">{c.ctaBtn}</Link>
          </div>
        </section>

        <section className="mg-section">
          <div className="mg-container">
            <span className="mg-label">{c.relatedLabel}</span>
            <h2 className="mg-h2" style={{ marginBottom:24 }}>{c.relatedTitle}</h2>
            <GuideCards items={c.related} />
          </div>
        </section>

      </div>
    </Layout>
  )
}

export default EditorialMethodPage
